import { useState, useEffect, useCallback } from 'react';
import { useAppToken } from './useAppToken';
import { useUser } from './useUser';

/**
 * Custom hook for subscription management
 *
 * Provides subscription status, trial information, and checkout functions
 * across the entire application.
 *
 * Usage:
 *   const { subscription, isTrialing, daysRemaining, loading, refreshSubscription } = useSubscription();
 */
export function useSubscription() {
  const { getToken } = useAppToken();
  const { user } = useUser();
  const isAuthenticated = !!user;
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || '';

  /**
   * Fetch subscription status from backend
   */
  const fetchSubscription = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const token = await getToken();

      const response = await fetch(`${API_URL}/api/users/subscription`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
        setError(null);
      } else if (response.status === 404) {
        // No subscription yet - this is OK
        setSubscription(null);
        setError(null);
      } else {
        throw new Error('Failed to fetch subscription');
      }
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user, getToken, API_URL]);

  // Fetch subscription on mount and when auth changes
  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  /**
   * Start checkout session
   */
  const startCheckout = useCallback(async (plan = 'personal') => {
    try {
      setCheckoutLoading(true);

      // NOTE: /api/stripe/create-checkout-session is a public endpoint (no JWT required).
      // We send the user's authentik sub + email directly from the user record so the
      // checkout call never blocks on token refresh issues.
      const response = await fetch(`${API_URL}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          authentik_sub: user?.authentikSub,
          email: user?.email,
          plan,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to create checkout session');
      }

      const data = await response.json();

      // Redirect to checkout URL (loading state will persist until redirect completes)
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('Error starting checkout:', err);
      setCheckoutLoading(false); // Only clear on error; on success we're redirecting anyway
      throw err;
    }
  }, [user, API_URL]);

  /**
   * Open customer portal
   */
  const openCustomerPortal = useCallback(async () => {
    try {
      setPortalLoading(true);
      const token = await getToken();

      const response = await fetch(`${API_URL}/api/stripe/create-portal-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          authentik_sub: user?.authentikSub,
          returnUrl: window.location.href,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create portal session');
      }

      const data = await response.json();

      // Redirect to customer portal (loading state will persist until redirect completes)
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('Error opening customer portal:', err);
      setPortalLoading(false); // Only clear on error; on success we're redirecting anyway
      throw err;
    }
  }, [getToken, user, API_URL]);

  /**
   * Re-fetch subscription from backend.
   *
   * Post-Authentik-migration there is no separate "sync from Stripe" path -
   * Stripe is wired straight to Postgres via webhook, so the canonical state
   * is always in /api/users/subscription. This is kept as an alias for
   * fetchSubscription so callers (AccountManagement) don't break.
   */
  const syncSubscription = useCallback(async () => {
    return fetchSubscription();
  }, [fetchSubscription]);

  // Computed properties
  const hasSubscription = subscription?.hasSubscription || false;
  const isTrialing = subscription?.subscription?.is_trialing || false;
  const daysRemaining = subscription?.subscription?.days_remaining_in_trial || 0;
  const status = subscription?.subscription?.status || null;
  const isActive = status === 'active' || status === 'trialing';
  const trialEndingSoon = isTrialing && daysRemaining <= 3;

  return {
    subscription: subscription?.subscription || null,
    hasSubscription,
    isTrialing,
    daysRemaining,
    status,
    isActive,
    trialEndingSoon,
    loading,
    checkoutLoading,
    portalLoading,
    error,
    refreshSubscription: fetchSubscription,
    syncSubscription,
    startCheckout,
    openCustomerPortal,
  };
}

export default useSubscription;
